import type { EmailMessageSearchRequest } from '@Notifications/api/EmailMessageApi';
import type {
  SdkEmailMessagesFilters as EmailMessagesFilters,
  SdkEmailMessagesOptions,
  SdkEmailRecipientRole as EmailRecipientRole
} from '@Pages/pageRuntime/sdk/types';

type Search = Diagonal.Notifications.Data.EmailMessageSearchData;
const ALL_RECIPIENT_ROLES: EmailRecipientRole[] = ['to', 'cc', 'bcc'];

type AppliedSearch = {
  search: Search;
  status: string;
  page: number;
};

function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function searchFromFilters(filters: EmailMessagesFilters | undefined): AppliedSearch {
  return {
    search: {
      resource_id: filters?.resourceId ?? null,
      records: [],
      subject_contains: filters?.subject ?? null,
      emails: filters?.emails ?? [],
      recipient_roles: filters?.recipientRoles ?? ALL_RECIPIENT_ROLES,
      from_address: filters?.fromAddress ?? null,
      statuses: [],
      date_from: filters?.dateFrom ?? null,
      date_before: filters?.dateBefore ?? null,
      timezone: filters?.timezone ?? browserTimeZone(),
      include_current_addresses: filters?.includeCurrentAddresses ?? false,
      email_field_id: filters?.emailFieldId ?? null,
      additional_addresses: filters?.additionalAddresses ?? [],
      excluded_addresses: filters?.excludedAddresses ?? []
    },
    status: filters?.status ?? 'all',
    page: 1
  };
}

/**
 * The request for one applied search. Only set filters are sent, so a plain record
 * history stays the minimal `records` + paging request the API defaults expect.
 */
export function buildSearchRequest(
  applied: AppliedSearch,
  records: Search['records'],
  perPage: number,
  canExpandAddresses: boolean
): EmailMessageSearchRequest {
  const { search, status, page } = applied;
  const request: EmailMessageSearchRequest = {};
  if (records.length > 0) {
    request.records = records;
  }
  if (search.resource_id) {
    request.resource_id = search.resource_id;
  }
  if (search.subject_contains) {
    request.subject_contains = search.subject_contains;
  }
  if (search.from_address) {
    request.from_address = search.from_address;
  }
  if (status !== 'all') {
    request.status = status;
  }
  if (search.emails.length > 0) {
    request.emails = search.emails;
    request.recipient_roles = search.recipient_roles;
  }
  if (search.date_from || search.date_before) {
    request.date_from = search.date_from;
    request.date_before = search.date_before;
    request.timezone = search.timezone;
  }
  if (canExpandAddresses && search.include_current_addresses && records.length > 0) {
    request.include_current_addresses = true;
    request.email_field_id = search.email_field_id;
    if (search.recipient_roles.length < ALL_RECIPIENT_ROLES.length) {
      request.recipient_roles = search.recipient_roles;
    }
    if (search.additional_addresses.length > 0) {
      request.additional_addresses = search.additional_addresses;
    }
    if (search.excluded_addresses.length > 0) {
      request.excluded_addresses = search.excluded_addresses;
    }
  }
  request.page = page;
  request.per_page = perPage;
  return request;
}

export function scopeRecords(props: SdkEmailMessagesOptions): Search['records'] {
  if (props.records) {
    return props.records.map((record) => ({ id: record.recordId, resource_id: record.resourceId }));
  }
  if (props.resourceId && props.recordId) {
    return [{ id: props.recordId, resource_id: props.resourceId }];
  }
  return [];
}

export function scopeProblem(
  props: SdkEmailMessagesOptions,
  records: Search['records'],
  canViewEmailHistory: boolean
): string | null {
  if (props.workspace && records.length > 0) {
    return 'EmailMessages takes either records or the workspace scope, not both.';
  }
  if (!props.workspace && records.length === 0 && props.records === undefined) {
    return 'EmailMessages needs a record, records, or workspace scope.';
  }
  if (props.workspace && !canViewEmailHistory) {
    return 'Workspace-wide email history needs the Manage Notifications permission.';
  }
  return null;
}
